/**
 * Security utilities for BCN application
 * Handles HTTPS enforcement, CSP headers, and security measures
 */

// Detect if running in the native app WebView (not mobile Safari/Chrome)
export const isMobileApp = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera || '';

  const isAndroidWebView = /wv/.test(userAgent);
  const isCustomAppUA = /BCNMobileApp|BCNIndiaApp|BCNApp/i.test(userAgent);
  // iOS WKWebView in apps usually omits "Safari" from the UA string
  const isIosWebView =
    /iPhone|iPad|iPod/.test(userAgent) &&
    !window.MSStream &&
    !/Safari|CriOS|FxiOS|EdgiOS/.test(userAgent);

  return isAndroidWebView || isCustomAppUA || isIosWebView;
};

// Force HTTPS in production (disabled for browser to avoid nginx conflicts)
export const enforceHTTPS = () => {
  // Only enforce HTTPS for mobile app, not for regular browser access
  if (isMobileApp() && 
      process.env.NODE_ENV === 'production' && 
      window.location.protocol === 'http:' && 
      window.location.hostname !== 'localhost') {
    window.location.href = window.location.href.replace('http:', 'https:');
  }
};

// Check if current connection is secure
export const isSecureConnection = () => {
  return window.location.protocol === 'https:' || 
         window.location.hostname === 'localhost' ||
         window.location.hostname === '127.0.0.1';
};

// Validate URLs for HTTPS in production
export const validateSecureURL = (url) => {
  if (process.env.NODE_ENV === 'production') {
    try {
      const urlObj = new URL(url);
      if (urlObj.protocol === 'http:' && urlObj.hostname !== 'localhost') {
        return url.replace('http:', 'https:');
      }
    } catch (error) {
    }
  }
  return url;
};


// Content Security Policy configuration
export const getCSPConfig = () => {
  const baseCSP = {
    'default-src': ["'self'"],
    'script-src': [
      "'self'",
      "'unsafe-inline'", // Required for React
      "'unsafe-eval'",   // Required for some libraries
      'https://maps.googleapis.com',
      'https://maps.gstatic.com',
      'https://www.google-analytics.com',
      'https://www.googletagmanager.com',
      'https://cdnjs.cloudflare.com', // For Swiper and other CDN libraries
      'https://www.youtube.com', // For YouTube iframe API
      'https://checkout.razorpay.com', // For Razorpay
      'https://download.agora.io' // Agora RTC (meetings/chat)
    ],
    'style-src': [
      "'self'",
      "'unsafe-inline'", // Required for CSS-in-JS
      'https://fonts.googleapis.com',
      'https://fonts.gstatic.com',
      'https://cdnjs.cloudflare.com' // For Swiper CSS
    ],
    'font-src': [
      "'self'",
      'https://fonts.gstatic.com',
      'https://fonts.googleapis.com'
    ],
    'img-src': [
      "'self'",
      'data:',
      'https:',
      'blob:',
      'https://i.ytimg.com' // YouTube thumbnails
    ],
    'connect-src': [
      "'self'",
      process.env.REACT_APP_API_HOST || 'http://localhost:8080/api',
      'http://localhost:8080/api', // Allow localhost for development
      'https://api-dev.bcnindia.com', // Allow dev API (full domain)
      'https://api-test.bcnindia.com', // Allow test API (full domain)
      'https://api.bcnindia.com', // Allow production API (full domain)
      'https://maps.googleapis.com',
      'https://www.google-analytics.com',
      'https://www.youtube.com', // YouTube API calls
      'https://api.razorpay.com', // Razorpay payment API
      'https://lumberjack.razorpay.com' // Razorpay checkout telemetry
    ].filter(Boolean), // Remove any undefined/null values
    'frame-src': [
      "'self'",
      'https://www.google.com',
      'https://www.youtube.com',
      'https://youtube.com', // Alternative YouTube domain
      'https://api.razorpay.com', // Allow Razorpay payment iframe
      'https://checkout.razorpay.com'
    ],
    'object-src': ["'none'"],
    'base-uri': ["'self'"],
    'form-action': ["'self'"],
    'upgrade-insecure-requests': process.env.NODE_ENV === 'production' ? [] : null
  };

  // Filter out null values
  const filteredCSP = Object.fromEntries(
    Object.entries(baseCSP).filter(([_, value]) => value !== null)
  );

  return filteredCSP;
};

// Set security headers for production
export const setSecurityHeaders = () => {
  // Check if nuclear CSP override is already applied
  const nukeCSP = document.querySelector('meta[data-nuke="true"]');
  if (nukeCSP) {
    // Only add HSTS header in production
    if (process.env.NODE_ENV === 'production') {
      const hstsMeta = document.createElement('meta');
      hstsMeta.httpEquiv = 'Strict-Transport-Security';
      hstsMeta.content = 'max-age=31536000; includeSubDomains; preload';
      document.head.appendChild(hstsMeta);
    }
    return;
  }
  
  // Check if permissive CSP override is already applied
  const overrideCSP = document.querySelector('meta[data-override="true"]');
  if (overrideCSP) {
    // Only add HSTS header in production
    if (process.env.NODE_ENV === 'production') {
      const hstsMeta = document.createElement('meta');
      hstsMeta.httpEquiv = 'Strict-Transport-Security';
      hstsMeta.content = 'max-age=31536000; includeSubDomains; preload';
      document.head.appendChild(hstsMeta);
    }
    return;
  }

  if (process.env.NODE_ENV === 'production') {
    // Set CSP header
    const cspConfig = getCSPConfig();
    const cspString = Object.entries(cspConfig)
      .map(([key, values]) => `${key} ${Array.isArray(values) ? values.join(' ') : values}`)
      .join('; ');

    // Remove any existing CSP meta tags to avoid conflicts
    const existingCSPMeta = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
    if (existingCSPMeta) {
      existingCSPMeta.remove();
    }

    // Add CSP meta tag
    const cspMeta = document.createElement('meta');
    cspMeta.httpEquiv = 'Content-Security-Policy';
    cspMeta.content = cspString;
    document.head.appendChild(cspMeta);

    // Security headers configuration

    // Add HSTS header for production
    const hstsMeta = document.createElement('meta');
    hstsMeta.httpEquiv = 'Strict-Transport-Security';
    hstsMeta.content = 'max-age=31536000; includeSubDomains; preload';
    document.head.appendChild(hstsMeta);
  } else {
    // Development mode - set permissive CSP with explicit frame-src
    const permissiveCSP = "default-src 'self' 'unsafe-inline' 'unsafe-eval' data: blob: http: https:; connect-src 'self' http: https: ws: wss: https://api-dev.bcnindia.com https://api-test.bcnindia.com https://api.bcnindia.com; frame-src 'self' http: https: 'unsafe-inline'; script-src 'self' 'unsafe-inline' 'unsafe-eval' http: https:; style-src 'self' 'unsafe-inline' http: https:;";
    
    // Remove any existing CSP meta tags
    const existingCSPMeta = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
    if (existingCSPMeta) {
      existingCSPMeta.remove();
    }
    
    // Add permissive CSP for development
    const cspMeta = document.createElement('meta');
    cspMeta.httpEquiv = 'Content-Security-Policy';
    cspMeta.content = permissiveCSP;
    document.head.appendChild(cspMeta);
  }
};


// Initialize security measures
export const initializeSecurity = () => {
  const isMobile = isMobileApp();
  
  // Enforce HTTPS only for mobile app to avoid nginx conflicts
  enforceHTTPS();
  
  // CSP is enforced by nginx in production; only inject meta CSP in development
  if (process.env.NODE_ENV === 'development') {
    setSecurityHeaders();
  }
  
  // Log security status
  if (isSecureConnection()) {
    console.log('✅ Secure connection established');
  } else if (process.env.NODE_ENV === 'development') {
    console.log('⚠️ Development mode - running without HTTPS');
  } else {
    console.warn('⚠️ Insecure connection detected');
  }
  
  
  // Only apply strict security measures in production and for mobile app
  if (process.env.NODE_ENV === 'production' && isMobile) {
    // Prevent right-click context menu in production mobile app
    document.addEventListener('contextmenu', (e) => e.preventDefault());
    
    // Prevent F12, Ctrl+Shift+I, Ctrl+U in production mobile app
    document.addEventListener('keydown', (e) => {
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && e.key === 'I') ||
        (e.ctrlKey && e.key === 'u')
      ) {
        e.preventDefault();
      }
    });
    
    // Monitor for insecure connections
    setInterval(() => {
      if (!isSecureConnection()) {
        enforceHTTPS();
      }
    }, 30000); // Check every 30 seconds
  } else {
    // Development mode or browser - minimal security
    console.log('🔓 Running with minimal security (browser mode)');
  }
};
